import React, { useEffect, useState, useCallback } from "react";
import { ClipboardCheck, ChevronRight } from "lucide-react";
import { Card, Modal, Spinner } from "../lib/ui";
import { ClosingSummaryContent } from "./ClosingSummary";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS = {
  completed: { dot: "bg-emerald-500", label: "Selesai", tone: "text-emerald-700" },
  waiting: { dot: "bg-amber-500", label: "Menunggu Finalisasi", tone: "text-amber-700" },
  draft: { dot: "bg-rose-500", label: "Draft", tone: "text-rose-600" },
};

// Owner Dashboard — compact "Today's Closing Status" per branch. Clicking a branch
// opens its Daily Closing Summary (view only, reuses the shared closing component).
export default function ClosingStatusWidget() {
  const toast = useToast();
  const date = todayStr();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales/closing-status", { params: { date } }); setRows(data.branches || []); }
    catch { /* silent on dashboard */ } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const openBranch = async (b) => {
    setView(b); setDetail(null); setDetailLoading(true);
    try { const { data } = await api.get("/sales/closing-preview", { params: { date, branch_id: b.branch_id } }); setDetail(data); }
    catch { toast.error("Gagal memuat ringkasan tutup harian"); } finally { setDetailLoading(false); }
  };

  return (
    <Card data-testid="closing-status-widget">
      <div className="mb-3 flex items-center gap-2">
        <ClipboardCheck size={16} className="text-primary" />
        <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">Status Tutup Harian Hari Ini</h2>
      </div>
      {loading ? (
        <div className="flex h-24 items-center justify-center"><Spinner className="h-6 w-6" /></div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-mute">Belum ada cabang.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {rows.map((b) => {
            const st = STATUS[b.status] || STATUS.draft;
            return (
              <button key={b.branch_id} onClick={() => openBranch(b)} data-testid={`closing-status-branch-${b.branch_id}`}
                className="flex items-center justify-between rounded-xl border border-line px-4 py-3 text-left transition hover:border-primary hover:bg-app">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold text-ink"><span className={`h-2.5 w-2.5 shrink-0 rounded-full ${st.dot}`} /> {b.branch_name}</p>
                  <p className={`mt-0.5 truncate text-xs font-medium ${st.tone}`}>{st.label}{b.closing_number ? ` · ${b.closing_number}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap text-right">
                  <div>
                    <p className="text-xs text-ink-mute">{b.transactions} trx</p>
                    <p className="text-sm font-bold text-ink">{rp(b.total_sales)}</p>
                  </div>
                  <ChevronRight size={16} className="text-ink-mute" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Modal open={!!view} onClose={() => setView(null)} title={`Tutup Harian · ${view?.branch_name || ""}`} size="xl">
        {detailLoading || !detail
          ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : <ClosingSummaryContent data={detail} date={date} branchId={view?.branch_id} />}
      </Modal>
    </Card>
  );
}
